import { type FC } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useOrganizationUsersQuery,
  type OrganizationUsersItem,
} from "@/partner/entities/organizationUsers";
import { useUser } from "@/partner/entities/auth";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";

interface TeamListProps {
  onClickItem: (id: string) => void;
}

type MemberStatus = "active" | "disabled";

const STATUS_COLOR: Record<MemberStatus, "success" | "default"> = {
  active: "success",
  disabled: "default",
};

export const TeamList: FC<TeamListProps> = ({ onClickItem }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search, disabled },
  } = useFilter();

  const { data, loading, error } = useOrganizationUsersQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        search: typeof search === "string" && search ? search : undefined,
        disabled:
          disabled === "true" ? true : disabled === "false" ? false : undefined,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.organizationUsers?.nodes ?? [];
  const count = data?.organizationUsers?.count ?? 0;

  const formatDate = (value: string): string =>
    interpolationFormat(value, "short", i18n.language);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("team.list.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("team.columns.member")}</TableCell>
            <TableCell>{t("team.columns.roles")}</TableCell>
            <TableCell>{t("team.columns.status")}</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              {t("team.columns.createdAt")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.map((row: OrganizationUsersItem) => {
            const isYou = row.user.id === user.id;
            const status: MemberStatus = row.disabled ? "disabled" : "active";

            return (
              <TableRow key={row.id} hover>
                <TableCell sx={{ maxWidth: 280 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ItemTitleLink
                      to={`${PagePaths.team}?tab=details&id=${row.id}`}
                      onOpen={() => onClickItem(row.id)}
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                    >
                      {row.user.email}
                    </ItemTitleLink>
                    {isYou && (
                      <Chip size="small" label={t("team.status.you")} />
                    )}
                  </Stack>
                  {row.user.displayName && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {row.user.displayName}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    {row.roles.map((role) => (
                      <Chip
                        key={role}
                        size="small"
                        label={t(`team.roles.${role}.label`)}
                      />
                    ))}
                  </Stack>
                </TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[status]}
                    label={t(`team.status.${status}`)}
                  />
                  {row.welcomeEmailSentAt && (
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                    >
                      {t("team.status.invitedAt", {
                        date: formatDate(row.welcomeEmailSentAt),
                      })}
                    </Typography>
                  )}
                </TableCell>

                <TableCell align="right" sx={{ width: 160 }}>
                  <Typography variant="body2" color="textPrimary" noWrap>
                    {formatDate(row.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
